/**
 * The live preview: the clue as the recipient will see it.
 *
 * It renders the **real player** (`LevelPlay`, passed in from App so this module
 * never imports it back) inside a phone-sized stage that is scaled down and
 * cropped to the middle of the screen, where the clue card sits. Using the real
 * component is the whole point: a second implementation would drift from the
 * player the first time a theme changed (decision D5).
 *
 * The stage is inert — pointer events off, hidden from assistive tech, and
 * LevelPlay itself refuses to check answers in preview mode.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { findCardBox, frameBox } from "./frame";
import { C, FONT, RADIUS, TINT } from "../chassis/tokens";
import Icon from "../chassis/Icon";

const PHONE_W = 390;
const PHONE_H = 844;

export default function CluePreview({
  LevelPlay,
  themeVars,
  config,
  index = 0,
  height = 340,
  width = 342,
  onExpand,
  tilt = -0.6,
}) {
  const { t: tr } = useTranslation();
  const stageRef = useRef(null);
  // The card's frame once measured (frame.js); until then, the middle of the phone.
  const [frame, setFrame] = useState(null);
  const baseScale = width / PHONE_W;
  const windowH = Math.min(PHONE_H, height / baseScale);
  const offsetY = Math.max(0, (PHONE_H - windowH) / 2);

  useLayoutEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;
      const level = config && config.levels ? config.levels[index] : null;
      const box = findCardBox(stage, level ? level.question : "");
      if (!box || box.w < 40 || box.h < 40) return;
      const next = frameBox(box, { width, maxHeight: height, pad: 18, padTop: 46 });
      setFrame((prev) =>
        prev && Math.abs(prev.translateY - next.translateY) < 1 && Math.abs(prev.scale - next.scale) < 0.005 ? prev : next
      );
    };
    measure();
    // The players animate their card in; measure again once it has settled.
    const timers = [80, 450, 1000].map((ms) => setTimeout(measure, ms));
    return () => timers.forEach(clearTimeout);
  }, [config, index, themeVars, width, height]);

  const transform = frame
    ? `translate(${frame.offsetX}px, 0) scale(${frame.scale}) translate(${frame.translateX}px, ${frame.translateY}px)`
    : `scale(${baseScale}) translateY(${-offsetY}px)`;

  return (
    <div
      data-testid="clue-preview"
      style={{
        position: "relative", marginTop: 18, borderRadius: 32, border: `6px solid ${C.surface}`,
        background: (themeVars && themeVars["--color-bg"]) || "#FCF0F3",
        boxShadow: `0 30px 50px -28px ${TINT.accent(0.55)}, 0 2px 6px ${TINT.shadow(0.06)}`,
        transform: `rotate(${tilt}deg)`, overflow: "hidden",
      }}
    >
      {/* Inside the frame: the frame clips its content, so a tag hanging off the
          top edge would be cut in half. */}
      <span style={{ position: "absolute", top: 10, left: 0, right: 0, zIndex: 3, display: "flex", justifyContent: "flex-end", padding: "0 12px", pointerEvents: "none" }}>
        <span style={{ padding: "4px 10px", borderRadius: 999, background: TINT.shadow(0.82), color: C.white, fontFamily: FONT, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {tr("app.creator.preview.live")}
        </span>
      </span>

      <div style={{ width, height: frame ? frame.height : height, overflow: "hidden", position: "relative" }}>
        <div
          ref={stageRef}
          aria-hidden="true"
          style={{
            position: "absolute", top: 0, left: 0,
            width: PHONE_W, height: PHONE_H,
            transform,
            transformOrigin: "top left",
            pointerEvents: "none", userSelect: "none",
            ...themeVars,
          }}
        >
          <LevelPlay config={config} startIndex={index} preview onFinale={() => {}} />
        </div>
      </div>

      {onExpand ? (
        <button
          type="button"
          onClick={onExpand}
          aria-label={tr("app.creator.preview.expand")}
          style={{
            position: "absolute", right: 10, bottom: 10, zIndex: 3, width: 36, height: 36, borderRadius: RADIUS.pill,
            border: "none", background: TINT.glass(0.92), boxShadow: `0 6px 16px -8px ${TINT.shadow(0.4)}`,
            display: "grid", placeItems: "center", cursor: "pointer",
          }}
        >
          <Icon name="expand" size={15} color={C.ink} strokeWidth={2.2} />
        </button>
      ) : null}
    </div>
  );
}
